/**
 * Product-card pointer follower — a frosted warm-grey disc, labelled, that
 * tracks the mouse inside the card it is over and nowhere else.
 *
 * SCOPE: .ccard, which is every standard product card on the site — the
 * homepage rail, collection grids, featured collections, search results,
 * recommendations and the cart cross-sell. One selector, because one snippet
 * renders all of them. Nothing else opts in: not section wrappers, not the
 * gaps between cards, not the menu's navigation tiles.
 *
 * ── CONTAINMENT ─────────────────────────────────────────────────────────────
 * The disc lives INSIDE the active card, in a clip layer appended to
 * .ccard__inner (`overflow: hidden`, the card's own radius). It is not a
 * viewport-fixed element positioned to look like it is inside a card. So it
 * is clipped to the rounded boundary by the browser, and it cannot appear
 * over an adjacent card, the header, the menu or the discount tab — not
 * because the script is careful, but because a child cannot leave a box that
 * clips. .ccard__inner also sets `isolation: isolate`, so the disc's z-index
 * is scoped to its own card.
 *
 * ONE INSTANCE. A single clip layer is built once and MOVED between cards.
 * A page with sixty cards holds one element and one rAF loop, and crossing
 * quickly between cards cannot leave a second disc behind — there is only
 * ever one to leave.
 *
 * DELEGATION. Listeners are on the document, not on each card. Search
 * results, filtered collection pages and recommendations are injected after
 * this script runs; a querySelectorAll at startup would bind to whatever
 * existed at that moment and silently miss everything fetched later.
 *
 * ── MOUSE, NOT WIDTH ────────────────────────────────────────────────────────
 * Enablement is decided per EVENT, on pointerType, and not by a
 * `(hover: hover) and (pointer: fine)` media query. In Shopify's narrow
 * device preview the browser reports a coarse pointer while the person is
 * still holding a mouse, and the media query would switch the follower off
 * for them. pointerType is the honest signal: 'mouse' and 'pen' follow,
 * 'touch' never does, so a tap can never leave a disc stuck on a card.
 *
 * ── THE LOOP ────────────────────────────────────────────────────────────────
 * One rAF frame writes one composited value:
 *
 *     translate3d(x, y, 0) scale(s)
 *
 * Position lerps toward the pointer, frame-rate independently, so a 120Hz
 * display and a 60Hz one settle in the same time rather than the fast one
 * tracking twice as tightly. Scale and opacity run off a time-based
 * progress, eased, so the disc grows on enter and shrinks on leave.
 *
 * The pointer is stored in CLIENT coordinates and converted to card-local
 * coordinates each frame from the host's live rect. That is what makes a
 * scrolling rail correct: the card moves under a stationary pointer and the
 * disc stays under the pointer rather than riding along with the card.
 *
 * NEVER top/left/width/height in the loop — those re-lay-out the page every
 * frame. Size and font-size are written once per card, on enter.
 *
 * pointermove stores two numbers and a flag. It fires faster than the
 * display can show.
 */
(function () {
  "use strict";

  /* One system, once. A second copy would mean two discs and two loops
     fighting over the same element. */
  if (window.__calmyCardCursor) return;
  window.__calmyCardCursor = true;

  var CARD = ".ccard";
  var INNER = ".ccard__inner";

  /* ── WHERE THE FOLLOWER DOES NOT GO ────────────────────────────────────────
     The disc is a CAROUSEL affordance: it says "this row moves, drag it". The
     collection page is a static grid - two columns on a phone, four on a
     desktop, nothing to drag - so a bubble reading "swipe" there is an
     instruction the page cannot carry out.

     It was never markup in the card and it is not part of any carousel
     component: this file attaches it to `.ccard` anywhere on the site, and
     that is how it reached a grid it was never meant to. The collection card
     is the one card that carries .ccard--plain, so it is named here and the
     disc is never built for it - no element, no wrapper, no listener state.
     Not a display:none over something that still exists.

     Everything else is untouched, including the homepage "Calmy range"
     carousel, whose cards do not carry this class. */
  var SKIP = ".ccard--plain";

  /* The card under a point, unless it is one of the skipped ones. Used by both
     entry paths - the pointer moving and the page scrolling under a still
     pointer - so neither can reach a card the other would refuse. */
  function pick(el) {
    if (!el || !el.closest) return null;
    var c = el.closest(CARD);
    if (!c || c.closest(SKIP)) return null;
    return c;
  }
  /* Anything actionable inside a card. Over these the follower is HIDDEN and
     the ordinary pointer comes back: the disc used to dim over the buy pill,
     which left a translucent circle sitting on the control the customer was
     about to press, and hid the cursor that tells them it is pressable. */
  /* ACTIONABLE CONTROLS INSIDE A CARD — and .ccard__inner is NOT one of them.

     The previous list included a bare `a[href]`, and the whole card IS an
     anchor (<a class="ccard__inner">). closest() therefore matched everywhere
     on the card, so the follower was hidden over the entire card instead of
     just over its controls, which is why it disappeared completely. */
  /* .ccard__link is the CARD'S OWN LINK, not a control inside it. The card
     stopped being one big <a class="ccard__inner"> when the cart button
     arrived - a button cannot live inside an anchor - so the anchor is now an
     overlay covering the whole card. Left in the bare `a[href]` case it would
     match everywhere again and hide the follower over the entire card, which
     is the exact bug the :not() below was written for in the first place. */
  /* .ccard__cart is in this list although it is a span and not a control.

     What the list actually selects is "places where the follower must get out
     of the way and the ordinary arrow must come back", and the homepage range
     card's cart mark is one: it sits inside the photograph, so `offMedia`
     says the pointer is over the picture and would keep the disc up - over
     the one thing on the picture the customer is aiming at. It is also the
     only element here that is deliberately not a button, which is why it
     needs naming rather than being caught by `button`. */
  var BUY = ".ccard__buy, .ccard__sw, .ccard__swatch, .ccard__swb, .ccard__cart, [data-calmy-cardadd], [data-calmy-quickadd], button, a[href]:not(.ccard__inner):not(.ccard__link)";
  /* A card is "in a carousel" when it sits in a horizontally scrolling rail.
     [data-calmy-rail] is the marker every rail already carries, so this
     needs no second attribute kept in sync by hand. The collection grid
     carries [data-calmy-swipe] but no rail: it is static, and it is
     deliberately not treated as swipeable here. */
  var RAIL = "[data-calmy-rail]";

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* ── sizing ─────────────────────────────────────────────────────────────
     From the CARD's width, never the window's: the same page shows a 169px
     two-column card and a 380px desktop card, and the disc has to belong to
     each. 24%, floored at 44 and capped at 112, gives 44 on the small
     column and 72 in the homepage rail. The label rides the same scale. */
  /* ~80px across on a product card, which is what the reference draws. 28% of
     the card's own width keeps it proportional between a 295px homepage card
     and a 289px collection card, and the clamp stops a very narrow or very
     wide card turning it into something else. */
  var PCT = 0.28, MIN = 72, MAX = 88, TMIN = 12, TMAX = 14;

  var LERP = 0.18;   // fraction of the remaining distance per 60Hz frame
  var GROW = 260;    // ms, enter and exit
  var SOFT = 160;    // ms, the fade-down over the buy pill
  /* 0, not 0.42: over an actionable control the disc goes away completely. */
  var SOFT_TO = 0;
  var MIN_SCALE = 0.62;
  /* One spacing unit of clearance between the disc's bottom edge and the top
     of the buy pill. Matches the card's own 12px rhythm. */
  var GAP = 12;
  /* The word in the disc where the card is NOT in a scrolling rail. Set to
     "swipe" for one label everywhere. */
  var STATIC_LABEL = "swipe";   /* one word everywhere, rails and grids alike */

  var clip = null, disc = null, txt = null;
  var card = null;    // the card the pointer is in, or null while leaving
  var host = null;    // the element holding the clip, kept through the exit
  var label = "";
  var size = 0, half = 0;
  var raf = 0, last = 0, srAF = 0;
  var phase = 0;      // 1 coming in, 0 going out
  var prog = 0;       // 0..1
  var softT = 0, softP = 0;
  var cx = -1, cy = -1;           // pointer, client coords
  var x = 0, y = 0;               // disc, card-local, lerped

  function build() {
    if (clip) return true;
    try {
      clip = document.createElement("div");
      clip.className = "cswipe";
      clip.setAttribute("aria-hidden", "true");
      disc = document.createElement("div");
      disc.className = "cswipe__d";
      txt = document.createElement("span");
      txt.className = "cswipe__t";
      disc.appendChild(txt);
      clip.appendChild(disc);
      return true;
    } catch (err) {
      /* Never leave a card with a hidden cursor and nothing in its place. */
      clip = disc = txt = null;
      return false;
    }
  }

  // Decelerating — it arrives rather than ramps up.
  /* IS THE POINTER IN THE PHOTOGRAPH? The disc is a swipe affordance for the
     rail of pictures, so it belongs over the pictures and nowhere else. Away
     from the image box - on the title, the rating, the price, the description
     or the cart button - it fades out and the ordinary cursor returns, which
     is also what makes those controls look pressable.

     Geometry rather than e.target, for the same reason calmy-cardswap.js uses
     geometry: the card's link is an overlay above the media and the media is
     pointer-events: none, so e.target over the photograph is the link, not
     the picture. */
  function offMedia(c, px, py) {
    if (!c) return 1;
    var m = c.querySelector(".ccard__media");
    if (!m) return 0;
    var r = m.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return 0;
    return px >= r.left && px <= r.right && py >= r.top && py <= r.bottom ? 0 : 1;
  }

  function ease(t) { return 1 - Math.pow(1 - t, 3); }
  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

  function draw() {
    if (!disc) return;
    var e = ease(prog);
    disc.style.opacity = e * (1 - (1 - SOFT_TO) * softP);
    /* Over the pill the WORD goes entirely, not just down with the disc.
       Two labels on the same 44px of pill — the disc's "swipe" sitting on
       the button's "Buy · $39" — is unreadable at any opacity that still
       leaves the circle visible, and the button's own label is the one that
       has to survive. The circle stays, dimmed; the word inside it does
       not. */
    txt.style.opacity = 1 - softP;
    disc.style.transform =
      "translate3d(" + (x - half).toFixed(2) + "px," + (y - half).toFixed(2) +
      "px,0) scale(" + (MIN_SCALE + (1 - MIN_SCALE) * e).toFixed(4) + ")";
  }

  function frame(ts) {
    /* Clamped: a backgrounded tab hands back a gap of seconds, which would
       otherwise jump the whole animation in one step. */
    var dt = last ? Math.min(ts - last, 50) : 16.7;
    last = ts;

    /* The active card can be replaced under a stationary pointer — a
       collection filter, a section reload, a cart re-render. pointerout
       fires for none of those. */
    if (host && !host.isConnected) { reset(); return; }

    if (host) {
      var r = host.getBoundingClientRect();
      var tx = cx - r.left, ty = cy - r.top;
      /* Frame-rate independent: the same settling time at 60Hz and 120Hz. */
      var k = 1 - Math.pow(1 - LERP, dt / 16.7);
      x += (tx - x) * k;
      y += (ty - y) * k;
    }

    prog = clamp(prog + (phase ? 1 : -1) * (dt / GROW), 0, 1);
    softP += (softT - softP) * clamp(dt / SOFT, 0, 1);
    draw();

    if (!phase && prog === 0) { reset(); return; }
    raf = window.requestAnimationFrame(frame);
  }

  function start() {
    if (raf) return;
    last = 0;
    raf = window.requestAnimationFrame(frame);
  }

  function enter(c, px, py, target) {
    /* A menu, cart or modal owns the screen while it is open. */
    if (document.body.classList.contains("calmy-lock")) return;
    /* Restated here as well as in pick(), so a future third entry path cannot
       reintroduce the disc on a grid card by forgetting the filter. */
    if (c.closest(SKIP)) return;
    if (!build()) return;

    var inner = c.querySelector(INNER) || c;
    var r = inner.getBoundingClientRect();
    if (!r.width || !r.height) return;

    size = Math.round(clamp(r.width * PCT, MIN, MAX));
    half = size / 2;
    disc.style.width = disc.style.height = size + "px";
    txt.style.fontSize =
      (TMIN + (TMAX - TMIN) * ((size - MIN) / (MAX - MIN))).toFixed(1) + "px";

    /* "swipe" where there is somewhere to swipe to, "view" in a static grid,
       where swiping does nothing and the card's actual affordance is
       opening it. Flip STATIC_LABEL to "swipe" for one word everywhere. */
    var want = c.closest(RAIL) ? "swipe" : STATIC_LABEL;
    if (want !== label) { label = want; txt.textContent = want; }

    try {
      /* A container re-rendered from cloned markup can arrive with a stale
         disc already inside it. Clear before attaching, or the page ends up
         with two. */
      sweep();
      inner.appendChild(clip);
    } catch (err) {
      c.classList.remove("cswipe-host");
      return;
    }
    host = inner;
    card = c;

    /* SNAP the position, so the disc grows where the pointer actually is
       instead of gliding in from the last card or from the origin. Scale
       and opacity keep their current value, so re-entering mid-exit
       continues smoothly rather than restarting. */
    cx = px; cy = py;
    x = px - r.left;
    y = py - r.top;
    softP = softT = (target && target.closest && target.closest(BUY)) || offMedia(c, px, py) ? 1 : 0;
    phase = 1;
    draw();

    /* Only now — the disc exists and is attached. */
    c.classList.add("cswipe-host");
    start();
  }

  function leave() {
    if (!card) return;
    card.classList.remove("cswipe-host");
    card = null;
    softT = 0;
    phase = 0;
    /* host stays until the exit finishes, so the disc fades out where it
       was rather than vanishing. */
    start();
  }

  /* Everything down, immediately and without an exit: the reason to hide is
     that nobody can see it. */
  function reset() {
    if (raf) { window.cancelAnimationFrame(raf); raf = 0; }
    phase = 0; prog = 0; softP = 0; softT = 0;
    card = null;
    host = null;
    if (disc) { disc.style.opacity = 0; }
    sweep();
    var hosts = document.querySelectorAll(".cswipe-host");
    for (var i = 0; i < hosts.length; i++) hosts[i].classList.remove("cswipe-host");
  }

  /* EVERY .cswipe in the document, not only the one this closure is holding.

     The live clip layer is a real child of a real card, so anything that
     re-renders a container by reading its markup back out and writing it
     again — `el.innerHTML = el.innerHTML`, a clone into a filtered grid, an
     editor section swap — copies the disc along with the cards. The copy is
     a stranger to this script: no variable points at it, so removing only
     `clip` left it on the page as a second, frozen disc.

     There is only ever one live layer, so "remove all of them" is exactly
     right, and it costs one querySelectorAll on a path that runs when the
     follower is being torn down rather than while it is moving. */
  function sweep() {
    var all = document.querySelectorAll(".cswipe");
    for (var i = 0; i < all.length; i++) {
      if (all[i].parentNode) all[i].parentNode.removeChild(all[i]);
    }
  }

  /* ── input ────────────────────────────────────────────────────────────── */

  function mouse(e) { return e.pointerType !== "touch"; }

  document.addEventListener("pointerover", function (e) {
    if (!mouse(e) || reduce.matches) return;
    var c = pick(e.target);
    if (c === card) return;
    if (card) leave();
    if (c) enter(c, e.clientX, e.clientY, e.target);
  });

  document.addEventListener("pointerout", function (e) {
    if (!mouse(e) || !card) return;
    /* pointerout also fires crossing between two elements INSIDE the same
       card — image to title, title to pill. Only a relatedTarget outside
       the card is a real departure. A null relatedTarget is the pointer
       leaving the window entirely, which is one. */
    if (e.relatedTarget && card.contains(e.relatedTarget)) return;
    leave();
  });

  document.addEventListener("pointermove", function (e) {
    if (!mouse(e)) return;
    cx = e.clientX; cy = e.clientY;
    if (!card) return;
    /* Dimmed over the pill so the label under it stays readable. The pill
       still takes its own hover and its own click: the clip layer and the
       disc are both pointer-events: none, so e.target is the real element
       underneath. */
    /* OVER the pill, or merely CLOSE to it. Testing e.target alone only
       fired once the pointer was already on the control, by which time the
       disc — a circle of radius `half` centred on the pointer — had its
       bottom edge lying across the pill for the previous ~32px of travel.
       That overlap is the collision; hiding it only on contact was always
       going to be too late.

       So the disc also fades once its own bottom edge comes within one
       spacing unit of the pill's top edge. GAP is that unit. Measured in
       client coordinates against the pill's live rect, so it follows the
       pill wherever the card puts it and needs no number kept in sync. */
    /* THE BUY PILL IS GONE, and with it the proximity test that used to live
       here: the disc faded as its bottom edge neared the pill's top edge,
       because the pill sat inside the card and the disc overlapped it before
       the pointer ever reached it. There is no pill to collide with now, and
       the boundary that matters is the photograph's own edge - which the
       disc should never cross anyway. */
    var over = (e.target && e.target.closest && e.target.closest(BUY)) || offMedia(card, cx, cy) ? 1 : 0;
    softT = over;
    /* The native cursor returns with it, so the control looks pressable. */
    if (card) card.classList.toggle("cswipe-host", softT === 0);
  }, { passive: true });

  /* SCROLL. A card can be scrolled out from under a stationary pointer and
     another scrolled in, and pointerover/pointerout fire for neither. One
     hit-test per frame of scrolling, not per scroll event. */
  function recheck() {
    srAF = 0;
    if (!card && !host) return;
    if (cx < 0) return;
    var el = document.elementFromPoint(cx, cy);
    var c = pick(el);
    if (c === card) return;
    if (card) leave();
    if (c) enter(c, cx, cy, el);
  }
  window.addEventListener("scroll", function () {
    if (srAF || (!card && !host)) return;
    srAF = window.requestAnimationFrame(recheck);
  }, { passive: true, capture: true });

  window.addEventListener("resize", reset, { passive: true });
  window.addEventListener("blur", reset);
  window.addEventListener("pagehide", reset);
  document.addEventListener("visibilitychange", function () {
    if (document.hidden) reset();
  });

  /* A menu, cart drawer or modal opening. calmy-core.js locks the body for
     all three, so one observer covers every overlay in the theme without
     this file needing to know their names. */
  if (window.MutationObserver) {
    new MutationObserver(function () {
      if (document.body.classList.contains("calmy-lock")) reset();
    }).observe(document.body, { attributes: true, attributeFilter: ["class"] });
  }

  /* Section reload in the editor, and filtered/paginated collection
     re-renders that dispatch it. The loop's isConnected check already
     catches the host being replaced; this makes it immediate. */
  document.addEventListener("shopify:section:load", reset);
  document.addEventListener("shopify:section:unload", reset);


  /* ── the touch hint ─────────────────────────────────────────────────────
     Not the follower and not a cursor: a static pill, shown once per rail on
     the first horizontal drag, that tracks nothing and is gone in a second.
     Only inside [data-calmy-rail] — a static grid gets none, because there
     is nothing there to swipe. */
  var hint = null, hintT = 0, hintOff = 0;

  function showHint(rail) {
    if (reduce.matches || rail.__calmyHinted) return;
    rail.__calmyHinted = true;
    var r = rail.getBoundingClientRect();
    if (!r.width) return;
    if (!hint) {
      hint = document.createElement("div");
      hint.className = "cswipe-hint";
      hint.setAttribute("aria-hidden", "true");
      hint.textContent = "swipe";
      document.body.appendChild(hint);
    }
    hint.style.left = (r.left + r.width / 2) + "px";
    hint.style.top = (r.top + r.height / 2) + "px";
    // Next frame, so the transition has a state to move away from.
    window.requestAnimationFrame(function () { hint.classList.add("is-on"); });
    window.clearTimeout(hintT);
    window.clearTimeout(hintOff);
    hintT = window.setTimeout(function () {
      hint.classList.remove("is-on");
      hintOff = window.setTimeout(function () {
        if (hint && hint.parentNode) hint.parentNode.removeChild(hint);
        hint = null;
      }, 260);
    }, 1100);
  }

  var tx0 = 0, ty0 = 0, trail = null, tdone = false;

  document.addEventListener("touchstart", function (e) {
    if (!e.touches || e.touches.length !== 1) { trail = null; return; }
    var t = e.touches[0];
    tx0 = t.clientX; ty0 = t.clientY; tdone = false;
    trail = e.target && e.target.closest ? e.target.closest(RAIL) : null;
    /* A touch must never leave a disc behind. */
    reset();
  }, { passive: true });

  document.addEventListener("touchmove", function (e) {
    if (!trail || tdone || !e.touches || e.touches.length !== 1) return;
    var t = e.touches[0];
    var dx = Math.abs(t.clientX - tx0), dy = Math.abs(t.clientY - ty0);
    /* Horizontal, and decisively so — a vertical page scroll that drifts a
       few pixels sideways is not a swipe. Native scrolling in both
       directions is untouched: this listener is passive and reads only. */
    if (dx > 12 && dx > dy * 1.5) { tdone = true; showHint(trail); }
    else if (dy > 16) { tdone = true; }
  }, { passive: true });

  document.addEventListener("touchend", function () { trail = null; }, { passive: true });
})();
